/* eslint-disable max-lines-per-function */
import {Button, Grid, Paper, Theme, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {createNotarySealApi, getNotarySealApi} from '../script/person-service';
import {addNotifications} from '../store/appActions';
import ConfirmationDialog from './ConfirmationDialog';
import PasswordField from './PasswordField';

const useStyles = makeStyles((theme: Theme) => ({
  title: {paddingTop: 0, paddingBottom: '1%', fontWeight: 550},
  subtitle: {paddingTop: 15, paddingBottom: '1%'},
  paper: {padding: theme.spacing(3, 2), width: 'inherit', marginTop: '0.1em'},
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  }
}));

const renderSealApiFields = (sealApi, addNotification) => {
  if (!sealApi) {
    return null;
  }
  return (
    <Grid container item xs={12} spacing={3}>
      <Grid item xs={4}>
        <PasswordField
          label={locale.notarySealApiKey}
          value={sealApi.key}
          addNotification={addNotification}
        />
      </Grid>
      <Grid item xs={4}>
        <PasswordField
          label={locale.notarySealApiSecret}
          value={sealApi.secret}
          addNotification={addNotification}
        />
      </Grid>
    </Grid>
  );
};

/**
 * Notary seal api management fields.
 * @return {*} the rendered string.
 */
function NotaryApiManagement(props) {
  const classes = useStyles();
  const [sealApi, setSealApi] = useState();
  const [sealApiFetched, setSealApiFetched] = useState(false);
  const [sealApiCreating, setSealApiCreating] = useState(false);
  const [submitDialog, setSubmitDialog] = React.useState(false);

  const addApiNotification = (notifications) =>
    props.dispatch(addNotifications(notifications));

  const handleSubmit = async () => {
    try {
      setSubmitDialog(false);
      setSealApiCreating(true);
      const response = await createNotarySealApi();

      if (response.key && response.secret) {
        setSealApi(response);
        addApiNotification([
          new Notification(
            locale.notarySealApiCreateSuccessNotification,
            'success',
            6000
          )
        ]);
      }
    } catch (error) {
      addApiNotification([
        new Notification(
          locale.notarySealApiCreateErrorNotification,
          'error',
          6000
        )
      ]);
    } finally {
      setSealApiCreating(false);
    }
  };

  useEffect(() => {
    if (sealApi) {
      return;
    }

    getNotarySealApi()
      .then((response) => {
        if (response.key && response.secret) {
          setSealApi(response);
        } else {
          setSealApi();
        }
        setSealApiFetched(true);
      })
      .catch((error) => {
        console.log(error);
        setSealApi();
        setSealApiFetched(false);
      });
  }, [sealApi, setSealApi, setSealApiFetched]);

  return (
    <Paper className={classes.paper}>
      <Typography variant='subtitle1' gutterBottom className={classes.title}>
        {locale.notarySealApiSectionTitle}
      </Typography>
      <Grid container item xs={12} spacing={3}>
        <Grid item xs={9}>
          <Typography
            variant='subtitle2'
            gutterBottom
            className={classes.subtitle}
          >
            {locale.notarySealApiSectionSubtitle}
          </Typography>
        </Grid>
        <Grid item xs={3} className={classes.row}>
          <Button
            variant='contained'
            color='primary'
            size='small'
            style={{width: 140, marginTop: 10}}
            onClick={sealApi ? () => setSubmitDialog(true) : handleSubmit}
            disabled={!sealApiFetched || sealApiCreating}
          >
            {locale.notarySealApiCreateButton}
          </Button>
        </Grid>
      </Grid>
      {renderSealApiFields(sealApi, addApiNotification)}
      <ConfirmationDialog
        open={submitDialog}
        dialogText={locale.notarySealApiCreateWarning}
        title={locale.notarySealApiDialogTitle}
        handleClose={() => setSubmitDialog(false)}
        handleSubmit={handleSubmit}
      />
    </Paper>
  );
}

/**
 * Map the properties.
 *
 * @param {*} dispatch
 * @return {Function} - The add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    dispatch: dispatch
  };
}

NotaryApiManagement.propTypes = {
  dispatch: PropTypes.func.isRequired
};

export default connect(null, mapDispatchToProps)(NotaryApiManagement);
